import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';

import { getFormValues } from 'redux-form';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import Filter from 'components/common/Filter';
import TabNavigation from 'components/common/TabNavigation';
import FacilitiesList from 'components/settings/FacilitiesList';
import FormError from 'components/common/FormError';
import Link from 'components/common/Link';

import { fetchFacilities } from 'redux/modules/facilities';
import { fetchBusiness } from 'redux/modules/business';

import { getFilterChangeTypes } from 'utils/filterChangeTypes';
import throttle from 'utils/throttle';

import { appTitle, titleSeparator } from 'config/helmetSettings';

const styles = () => ({
  formError: {
    marginTop: 15,
    marginBottom: 15,
  },
});

class BusinessFacilities extends Component {
  constructor(props) {
    super(props);

    this.state = {
      limit: 10,
      offset: 0,
      triggerLoading: true,
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onPageScrollFetchRequest = this.onPageScrollFetchRequest.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentDidMount() {
    this.onRequest();
    window.addEventListener('scroll', this.onPageScrollFetchRequest);
    this.props.actions.fetchBusiness({
      triggerLoading: true,
    });
  }

  componentWillReceiveProps(nextProps) {
    const { count, loading, filters } = this.props;
    const filtersChanged = nextProps.filters['Facility Name'] !== filters['Facility Name'];
    const newState = {};

    if (filtersChanged) {
      newState.offset = 0;
    }
    if (count === 0) {
      newState.triggerLoading = true;
    } else {
      newState.triggerLoading = false;
    }
    if (nextProps.loading < loading) {
      newState.triggerLoading = false;
    }

    this.setState(newState);
  }

  componentDidUpdate(prevProps, prevState) {
    const { filters } = this.props;
    const { limit, offset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    const { throttledFiltersChanged, filtersChanged } = getFilterChangeTypes(
      prevProps.filters,
      filters,
      {
        textFilterKeys: ['Facility Name'],
      },
    );

    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (limitChanged || offsetChanged || filtersChanged) {
      this.onRequest();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onPageScrollFetchRequest);
  }

  onRequest() {
    const { actions, filters } = this.props;
    const { limit, offset, triggerLoading } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map(key => ({
        name: key,
        operation: 'contains',
        val: filters[key],
        case_sensitivity: false,
      })) : null;
    actions.fetchFacilities({
      limit,
      offset,
      filters: requestFilters,
      triggerLoading,
      push: offset > 0,
    });
  }

  onPageScrollFetchRequest() {
    const { offset, limit } = this.state;
    const documentHeight = document.body.getBoundingClientRect().height;
    const documentTop = document.body.getBoundingClientRect().top;
    const windowHeight = window.innerHeight;
    if ((windowHeight - documentTop) / documentHeight > 0.8) {
      if ((this.props.facilities.length - limit) >= offset) {
        this.setState({ offset: offset + limit });
      }
    }
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  render() {
    const { facilities, business, loading, account, classes, width, filters } = this.props;
    const { mobileFilterExpanded } = this.state;
    return (
      <Layout variant="settings">
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Facilities
          </title>
        </Helmet>
        <TabNavigation
          items={[
            {
              text: 'General',
              href: '/settings/business/general',
            },
            {
              text: 'Facilities',
              href: '/settings/business/facilities',
            },
            ...account.Roles.filter(item =>
              item.Name === 'Master Administrator' || item.Name === 'Administrator').length > 0 ?
              [
                {
                  text: 'Claim MD Credentials',
                  href: '/settings/business/claim-md-credentials',
                },
                {
                  text: 'Twilio Credentials',
                  href: '/settings/business/twilio-credentials',
                },
              ] : [],
          ]}
        />
        <Filter
          form="facilitiesFilter"
          primaryButtonText="Add New"
          primaryButtonHref="/settings/business/facilities/create"
          placeholder="Search Facility"
          primaryButtonDisabled={account.Roles.filter(item =>
            item.Name === 'Master Administrator' || item.Name === 'Administrator').length === 0
           || !business.ID}
          {...width === 'sm' || width === 'xs' ? {
            top: 110,
            mobileFilterExpanded,
            onMobileFilterBottonClick: this.onMobileFilterBottonClick,
            filters,
          } : {
            top: 126,
          }}
        />
        {(loading > 0) ? (
          <Progress />
        ) : (
          <Fragment>
            {!business.ID &&
              <FormError className={classes.formError}>
                Please, fill
                {'\u00A0'}
                <Link
                  href="/settings/business/general"
                  size="lg"
                  underline
                  bold
                >
                  Business General
                </Link> 
                {'\u00A0'}
                settings to enable Facility creation!
              </FormError>
            }
            <FacilitiesList facilities={facilities} />
          </Fragment>
        )}
      </Layout>
    );
  }
}

BusinessFacilities.propTypes = {
  facilities: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  loading: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  actions: PropTypes.shape({
    fetchFacilities: PropTypes.func.isRequired,
    fetchBusiness: PropTypes.func.isRequired,
  }).isRequired,
  filters: PropTypes.shape({
    'Facility Name': PropTypes.string,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    formError: PropTypes.string.isRequired,
  }).isRequired,
};

const mapStateToProps = (store) => {
  const facilitiesFilterValues = getFormValues('facilitiesFilter')(store);
  return {
    facilities: store.facilities.data,
    business: store.business.data,
    count: store.facilities.count,
    account: store.account,
    loading: store.facilities.loading
      + store.business.loading,
    filters: {
      ...facilitiesFilterValues
        && facilitiesFilterValues.Filter
        ? { 'Facility Name': facilitiesFilterValues.Filter }
        : {},
    },
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ fetchFacilities, fetchBusiness }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(withWidth()(BusinessFacilities)),
);



// WEBPACK FOOTER //
// ./src/pages/settings/BusinessFacilities.js